import { getHipSpineStudy, putHipSpineStudy } from '@/api/hip-spine-study/hipSpineStudy';
import {
    getAllHipSpinePostures,
    resetHipSpinePosture,
} from '@/api/hip-spine-study/hipSpinePosture';
import {
    getHipSpineApprovedLandmarks,
    getHipSpineLandmarks,
    approveHipSpineLandmarks,
} from '@/api/hip-spine-study/hipSpineLandmark';
import type { PostureSavePlan } from './useHipSpinePostureSavePlan';
import { type HipSpineSafeZone } from '@/domain/anatomy/pelvis/hipSpineNomogramCalculations';
import { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import { applyPostureData, loadPostureViewState, resetPostureViewState } from './hipSpineServices';
import type { HipSpinePostureViewState, PosturesAndViewStates } from './types';
import { logger } from '@/util/logger';
import { asyncTimeout } from '@/util/asyncTimeout';
import { throwIfAborted } from '@/util/genericAbortSignal';
import { initialState, type HipSpineStoreContext, type PanelView } from './hipSpineStore';
import { persistPosture, refreshPosture } from './hipSpineStorePersistence';

const log = logger('hip-spine');

/** Poll every 10 seconds */
const POLL_INTERVAL_MS = 10_000;

/** 5-minute polling timeout */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function resetState(ctx: HipSpineStoreContext): void {
    Object.assign(ctx.state, initialState());
    for (const viewState of ctx.postureViewStates.values()) resetPostureViewState(viewState);
    ctx.postureViewStates.clear();
}

async function fetchPosturesAndViewStates(
    caseId: number,
    hipSpineStudyId: number,
    signal: AbortSignal,
): Promise<PosturesAndViewStates> {
    const postures = await getAllHipSpinePostures(caseId, hipSpineStudyId);
    const viewStateEntries = await Promise.all(
        postures.map(async (posture) => {
            const viewState = await loadPostureViewState(caseId, hipSpineStudyId, signal, posture);
            return [posture.posture, viewState] as const;
        }),
    );
    const viewStates = new Map(viewStateEntries);
    return { postures, viewStates };
}

function formatLoadError(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error);
    return `Failed to load hip spine study: ${message}`;
}

async function approveLandmarks(ctx: HipSpineStoreContext, posture: string): Promise<void> {
    const { state } = ctx;
    if (!state.caseId || !state.studyId) return;
    const approved = await approveHipSpineLandmarks(
        state.caseId,
        state.studyId,
        posture as (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    );
    const postureObj = state.hipSpinePostures.find((candidate) => candidate.posture === posture);
    if (postureObj) postureObj.has_unapproved_changes = false;
    const index = state.approvedLandmarks.findIndex((existing) => existing.posture === posture);
    if (index >= 0) {
        state.approvedLandmarks[index] = approved;
    } else {
        state.approvedLandmarks.push(approved);
    }
}

async function resetPostureToAi(ctx: HipSpineStoreContext, posture: string): Promise<void> {
    const { state, postureViewStates, abort } = ctx;
    if (!state.caseId || !state.studyId) return;
    const postureId = posture as (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
    // Cancellable on study-level teardown (route change / new study load) so a
    // reset in flight when the planner unmounts cannot mutate state afterwards.
    const signal = abort.controller?.signal;

    // Fetch BOTH the reset posture and its landmarks before committing either. The
    // reset is two separate calls (POST reset, then GET landmarks); committing the
    // posture DTO between them would leave a reset DTO paired with stale landmarks if
    // the GET fails (500 / network drop). Fetch-then-commit keeps the failure path at
    // a consistent pre-reset snapshot, matching persistPosture / refreshPosture.
    const updated = await resetHipSpinePosture(state.caseId, state.studyId, postureId, { signal });
    const { landmarks } = await getHipSpineLandmarks(state.caseId, state.studyId, postureId, {
        signal,
    });

    const index = state.hipSpinePostures.findIndex((candidate) => candidate.posture === posture);
    if (index >= 0) state.hipSpinePostures[index] = updated;
    // Apply server truth (filtered landmarks + geometry rebuild) the same way the
    // save/refresh actions do, so the action is self-contained regardless of caller.
    const viewState = postureViewStates.get(posture);
    if (viewState) applyPostureData(viewState, updated, landmarks);
}

/**
 * Load postures, view states, and approved landmarks for a completed study.
 * Does NOT abort or reset state — safe to call from the polling loop.
 */
async function loadStudyData(
    ctx: HipSpineStoreContext,
    caseId: number,
    studyId: number,
    signal: AbortSignal,
): Promise<void> {
    const { state, postureViewStates } = ctx;
    const data = await fetchPosturesAndViewStates(caseId, studyId, signal);
    state.hipSpinePostures = data.postures;
    for (const [key, viewState] of data.viewStates) postureViewStates.set(key, viewState);
    state.activePostureId = data.postures.some((posture) => posture.posture === 'standing-sagittal')
        ? 'standing-sagittal'
        : (data.postures[0]?.posture ?? null);

    const approvedResults = await Promise.allSettled(
        data.postures.map((posture) =>
            getHipSpineApprovedLandmarks(caseId, studyId, posture.posture),
        ),
    );
    for (const result of approvedResults) {
        if (result.status === 'fulfilled' && result.value) {
            state.approvedLandmarks.push(result.value);
        }
    }
}

async function loadHipSpineStudy(
    ctx: HipSpineStoreContext,
    caseId: number,
    studyId: number | null,
): Promise<void> {
    const { state } = ctx;
    ctx.abort.controller?.abort();
    resetState(ctx);
    if (!studyId) return;
    state.isLoading = true;
    const controller = new AbortController();
    ctx.abort.controller = controller;
    try {
        state.caseId = caseId;
        state.studyId = studyId;
        const study = await getHipSpineStudy(caseId, studyId, { signal: controller.signal });
        state.hipSpineStudy = study;

        if (study.resource_state !== 'Completed') {
            // Study not ready — store metadata for status display, skip data load
            return;
        }

        await loadStudyData(ctx, caseId, studyId, controller.signal);
    } catch (error) {
        if (!controller.signal.aborted) state.loadError = formatLoadError(error);
    } finally {
        state.isLoading = false;
    }
}

/**
 * Fetch the study once and handle terminal states.
 * Returns `true` when a terminal state was reached and polling should stop.
 */
async function pollIteration(
    ctx: HipSpineStoreContext,
    caseId: number,
    studyId: number,
    signal: AbortSignal,
): Promise<boolean> {
    const { state } = ctx;
    const study = await getHipSpineStudy(caseId, studyId, { signal });

    if (study.resource_state === 'Completed') {
        state.isLoading = true;
        try {
            state.hipSpineStudy = study;
            await loadStudyData(ctx, caseId, studyId, signal);
        } finally {
            state.isLoading = false;
        }
        return true;
    }

    if (study.resource_state === 'Error') {
        state.hipSpineStudy = study;
        return true;
    }

    // Still processing — update study metadata in case fields changed
    state.hipSpineStudy = study;
    return false;
}

/**
 * Poll the study until it reaches a terminal state ('completed' or 'error').
 * On 'completed', automatically loads full study data (postures, landmarks, images).
 * Cancelled by the store's abortController when route changes or a new study loads.
 */
async function pollStudyUntilTerminal(ctx: HipSpineStoreContext): Promise<void> {
    const { state } = ctx;
    const { caseId, studyId } = state;
    const signal = ctx.abort.controller?.signal;
    if (!caseId || !studyId || !signal || signal.aborted) return;

    const timeoutTime = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < timeoutTime) {
        throwIfAborted(signal);
        await asyncTimeout(POLL_INTERVAL_MS, signal);
        throwIfAborted(signal);

        try {
            if (await pollIteration(ctx, caseId, studyId, signal)) return;
        } catch (error) {
            log.warning('Transient error polling hip-spine study: %s', error);
        }
    }

    state.loadError =
        'Hip-Spine Analysis is taking longer than expected. Please refresh the page to check status.';
}

async function updateBounds(ctx: HipSpineStoreContext, bounds: HipSpineSafeZone): Promise<void> {
    const { state } = ctx;
    if (!state.caseId || !state.studyId) return;
    const updated = await putHipSpineStudy(state.caseId, state.studyId, {
        min_cup_anteversion_bound: bounds.minAnteversion,
        max_cup_anteversion_bound: bounds.maxAnteversion,
        min_cup_inclination_bound: bounds.minInclination,
        max_cup_inclination_bound: bounds.maxInclination,
    });
    state.hipSpineStudy = updated;
}

function setActivePostureId(ctx: HipSpineStoreContext, postureId: string | null): void {
    ctx.state.activePostureId = postureId;
}

function setActivePanelView(ctx: HipSpineStoreContext, view: PanelView): void {
    ctx.state.activePanelView = view;
}

function setSafeZoneSliderMode(ctx: HipSpineStoreContext, enabled: boolean): void {
    ctx.state.isSafeZoneSliderMode = enabled;
}

function viewState(
    ctx: HipSpineStoreContext,
    postureId: string,
): HipSpinePostureViewState | undefined {
    return ctx.postureViewStates.get(postureId);
}

function toggleAngleVisibility(ctx: HipSpineStoreContext, angleId: string): void {
    const { state, postureViewStates } = ctx;
    if (!state.activePostureId) return;
    const view = postureViewStates.get(state.activePostureId);
    if (!view) return;
    const angle = view.angles.find((candidate) => candidate.id === angleId);
    if (angle) angle.visible = !angle.visible;
}

/**
 * Bind the hip-spine store's actions to a shared {@link HipSpineStoreContext}.
 * Each returned action keeps the exact behaviour of the inline store actions it
 * replaces; this factory only closes them over `ctx` so the `defineStore` setup
 * callback stays within the function-length cap.
 */
export function createHipSpineStoreActions(ctx: HipSpineStoreContext) {
    return {
        approveLandmarks: (posture: string) => approveLandmarks(ctx, posture),
        resetPostureToAi: (posture: string) => resetPostureToAi(ctx, posture),
        loadHipSpineStudy: (caseId: number, studyId: number | null) =>
            loadHipSpineStudy(ctx, caseId, studyId),
        pollStudyUntilTerminal: () => pollStudyUntilTerminal(ctx),
        updateBounds: (bounds: HipSpineSafeZone) => updateBounds(ctx, bounds),
        persistPosture: (postureId: string, plan: PostureSavePlan, signal?: AbortSignal) =>
            persistPosture(ctx, postureId, plan, signal),
        refreshPosture: (postureId: string, signal?: AbortSignal) =>
            refreshPosture(ctx, postureId, signal),
        setActivePostureId: (postureId: string | null) => setActivePostureId(ctx, postureId),
        setActivePanelView: (view: PanelView) => setActivePanelView(ctx, view),
        setSafeZoneSliderMode: (enabled: boolean) => setSafeZoneSliderMode(ctx, enabled),
        viewState: (postureId: string) => viewState(ctx, postureId),
        toggleAngleVisibility: (angleId: string) => toggleAngleVisibility(ctx, angleId),
    };
}
